import type { InventoryManager } from '../items/InventoryManager';
import { ITEM_CATALOG, getSellPrice, type ItemId } from '../items/catalog';
import { GameState } from '../types';

export interface ShopUIHost {
  inventory: InventoryManager;
  getGameState(): GameState;
  getWallet(): number;
  sellRunItem(itemId: ItemId, amount: number): { ok: boolean; credits: number; reason?: string };
  requestPointerLock(): void;
  exitPointerLock(): void;
}

/**
 * Floor shop panel — pick amounts of run-bag materials to sell for credits.
 */
export class ShopUI {
  private readonly root: HTMLDivElement;
  private readonly host: ShopUIHost;
  private open = false;
  private unsub: (() => void) | null = null;
  private readonly selected = new Map<ItemId, number>();

  constructor(parent: HTMLElement, host: ShopUIHost) {
    this.host = host;

    this.root = document.createElement('div');
    this.root.id = 'shop-overlay';
    this.root.innerHTML = `
      <div class="shop-panel" role="dialog" aria-label="Floor Shop">
        <header class="shop-header">
          <div class="shop-title-block">
            <h2>Floor Shop</h2>
            <span class="shop-hint">Sell run loot for credits · Esc / E to close</span>
          </div>
          <button type="button" class="shop-close" data-close aria-label="Close shop">✕</button>
        </header>
        <p class="shop-blurb">Only items in your run bag can be sold. Banked materials stay put.</p>
        <div class="shop-list" data-shop-list></div>
        <footer class="shop-footer">
          <div class="shop-wallet" data-shop-wallet></div>
          <div class="shop-total" data-shop-total>Selected: 0¢</div>
          <button type="button" class="shop-sell-btn" data-shop-sell disabled>Sell selected</button>
        </footer>
      </div>
    `;
    parent.appendChild(this.root);

    this.root.addEventListener('pointerdown', (e) => {
      if (e.target === this.root) {
        e.preventDefault();
        this.setOpen(false);
        return;
      }
      const closeBtn = (e.target as HTMLElement | null)?.closest?.('[data-close]');
      if (closeBtn && this.root.contains(closeBtn)) {
        e.preventDefault();
        this.setOpen(false);
      }
    });

    const sellBtn = this.root.querySelector('[data-shop-sell]') as HTMLButtonElement;
    sellBtn.addEventListener('click', () => this.sellSelected());

    this.unsub = host.inventory.onChange(() => {
      if (this.open) this.refresh();
    });
    this.injectStyles();
  }

  get isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  setOpen(open: boolean): void {
    if (this.open === open) return;
    this.open = open;
    this.root.classList.toggle('open', open);
    if (open) {
      this.selected.clear();
      this.host.exitPointerLock();
      this.refresh();
    } else if (this.host.getGameState() === GameState.Diving) {
      this.host.requestPointerLock();
    }
  }

  refresh(): void {
    if (!this.open) return;

    const listEl = this.root.querySelector('[data-shop-list]') as HTMLElement;
    const walletEl = this.root.querySelector('[data-shop-wallet]') as HTMLElement;
    const totalEl = this.root.querySelector('[data-shop-total]') as HTMLElement;
    const sellBtn = this.root.querySelector('[data-shop-sell]') as HTMLButtonElement;

    const slots = this.host.inventory.listRunSlots().filter((s) => getSellPrice(s.itemId) > 0);

    // Clamp selections to current amounts.
    for (const [id, amount] of [...this.selected]) {
      const have = this.host.inventory.getRunAmount(id);
      if (have <= 0 || getSellPrice(id) <= 0) this.selected.delete(id);
      else if (amount > have) this.selected.set(id, have);
    }

    if (slots.length === 0) {
      listEl.innerHTML = `<div class="shop-empty">Nothing worth selling in your run bag.</div>`;
    } else {
      listEl.innerHTML = slots
        .map((s) => {
          const def = ITEM_CATALOG[s.itemId];
          const price = getSellPrice(s.itemId);
          const qty = this.selected.get(s.itemId) ?? 0;
          return `
            <div class="shop-row" data-item="${s.itemId}">
              <span class="swatch" style="background:${def.color}"></span>
              <div class="shop-meta">
                <strong>${def.name}</strong>
                <span class="shop-price">${price}¢ each · have ×${s.amount}</span>
              </div>
              <div class="shop-qty">
                <button type="button" data-adj="-1" data-item="${s.itemId}">−</button>
                <input type="number" min="0" max="${s.amount}" value="${qty}" data-qty="${s.itemId}" />
                <button type="button" data-adj="1" data-item="${s.itemId}">+</button>
                <button type="button" class="shop-all" data-all="${s.itemId}">All</button>
              </div>
              <div class="shop-line">${qty * price}¢</div>
            </div>
          `;
        })
        .join('');
    }

    listEl.querySelectorAll('[data-adj]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.item as ItemId;
        const delta = Number((btn as HTMLElement).dataset.adj);
        this.adjust(id, delta);
      });
    });

    listEl.querySelectorAll('[data-all]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.all as ItemId;
        const have = this.host.inventory.getRunAmount(id);
        if (have > 0) this.selected.set(id, have);
        else this.selected.delete(id);
        this.refresh();
      });
    });

    listEl.querySelectorAll('[data-qty]').forEach((input) => {
      input.addEventListener('change', () => {
        const el = input as HTMLInputElement;
        const id = el.dataset.qty as ItemId;
        const have = this.host.inventory.getRunAmount(id);
        const next = Math.max(0, Math.min(have, Number(el.value) || 0));
        if (next <= 0) this.selected.delete(id);
        else this.selected.set(id, next);
        this.refresh();
      });
    });

    const total = this.selectionTotal();
    walletEl.textContent = `Wallet: ${this.host.getWallet()} credits`;
    totalEl.textContent = `Selected: ${total}¢`;
    sellBtn.disabled = total <= 0;
  }

  dispose(): void {
    this.unsub?.();
    this.root.remove();
  }

  private adjust(id: ItemId, delta: number): void {
    const have = this.host.inventory.getRunAmount(id);
    const cur = this.selected.get(id) ?? 0;
    const next = Math.max(0, Math.min(have, cur + delta));
    if (next <= 0) this.selected.delete(id);
    else this.selected.set(id, next);
    this.refresh();
  }

  private selectionTotal(): number {
    let total = 0;
    for (const [id, amount] of this.selected) {
      total += amount * getSellPrice(id);
    }
    return total;
  }

  private sellSelected(): void {
    const entries = [...this.selected.entries()].filter(([, n]) => n > 0);
    if (entries.length === 0) return;

    let earned = 0;
    for (const [id, amount] of entries) {
      const result = this.host.sellRunItem(id, amount);
      if (result.ok) earned += result.credits;
    }
    this.selected.clear();
    this.refresh();
    if (earned > 0) {
      const totalEl = this.root.querySelector('[data-shop-total]') as HTMLElement;
      totalEl.textContent = `Sold for ${earned}¢`;
    }
  }

  private injectStyles(): void {
    if (document.getElementById('shop-styles')) return;
    const style = document.createElement('style');
    style.id = 'shop-styles';
    style.textContent = `
      #shop-overlay {
        position: absolute;
        inset: 0;
        display: none;
        align-items: center;
        justify-content: center;
        background: rgba(4, 8, 12, 0.72);
        z-index: 22;
        pointer-events: auto;
      }
      #shop-overlay.open { display: flex; }
      .shop-panel {
        width: min(560px, 94vw);
        max-height: min(720px, 88vh);
        overflow: auto;
        background: linear-gradient(160deg, #1a1410 0%, #0e0c0a 100%);
        border: 1px solid rgba(240, 196, 58, 0.28);
        padding: 18px 20px 20px;
        color: #f2ebe0;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      }
      .shop-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
        margin-bottom: 8px;
      }
      .shop-title-block {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
      }
      .shop-header h2 { margin: 0; font-size: 18px; font-weight: 600; color: #f0c43a; }
      .shop-hint { opacity: 0.55; font-size: 11px; }
      .shop-close {
        width: 40px;
        height: 40px;
        flex-shrink: 0;
        border-radius: 12px;
        border: 2px solid rgba(240, 196, 58, 0.28);
        background: rgba(240, 196, 58, 0.08);
        color: #f2ebe0;
        font: inherit;
        font-size: 18px;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
      }
      .shop-close:active { background: rgba(240, 196, 58, 0.2); }
      .shop-blurb { margin: 0 0 14px; font-size: 12px; opacity: 0.7; }
      .shop-list { display: flex; flex-direction: column; gap: 6px; min-height: 80px; }
      .shop-empty {
        padding: 18px 10px;
        text-align: center;
        opacity: 0.55;
        font-size: 12px;
        border: 1px dashed rgba(255,255,255,0.12);
      }
      .shop-row {
        display: grid;
        grid-template-columns: 12px 1fr auto auto;
        gap: 10px;
        align-items: center;
        padding: 8px 10px;
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(240, 196, 58, 0.12);
        font-size: 12px;
      }
      .shop-row .swatch { width: 12px; height: 12px; display: inline-block; }
      .shop-meta { display: flex; flex-direction: column; gap: 2px; }
      .shop-price { opacity: 0.55; font-size: 11px; }
      .shop-qty {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .shop-qty button {
        width: 26px;
        height: 26px;
        border: 1px solid rgba(240, 196, 58, 0.3);
        background: rgba(240, 196, 58, 0.08);
        color: #f2ebe0;
        cursor: pointer;
        font-size: 14px;
      }
      .shop-qty .shop-all {
        width: auto;
        padding: 0 8px;
        font-size: 11px;
      }
      .shop-qty input {
        width: 48px;
        height: 26px;
        text-align: center;
        background: rgba(0,0,0,0.35);
        border: 1px solid rgba(255,255,255,0.15);
        color: #f2ebe0;
        font: inherit;
      }
      .shop-line { min-width: 48px; text-align: right; color: #f0c43a; font-weight: 600; }
      .shop-footer {
        margin-top: 16px;
        display: grid;
        grid-template-columns: 1fr auto auto;
        gap: 12px;
        align-items: center;
      }
      .shop-wallet, .shop-total { font-size: 12px; opacity: 0.85; }
      .shop-total { color: #f0c43a; }
      .shop-sell-btn {
        padding: 8px 14px;
        border: 1px solid rgba(240, 196, 58, 0.45);
        background: rgba(240, 196, 58, 0.15);
        color: #ffe9a8;
        font: inherit;
        font-size: 12px;
        cursor: pointer;
      }
      .shop-sell-btn:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }
      .shop-sell-btn:not(:disabled):hover {
        background: rgba(240, 196, 58, 0.28);
      }
      @media (max-width: 560px) {
        .shop-row { grid-template-columns: 12px 1fr; }
        .shop-qty, .shop-line { grid-column: 2; }
        .shop-footer { grid-template-columns: 1fr; }
      }
    `;
    document.head.appendChild(style);
  }
}
